import { useAppSelector } from "../../../../../../../../stores/hooks/hooks.ts";
import { ResponsivePie } from "@nivo/pie";
import { useMemo, useState } from "react";
import { COMMUNITY_INSIGHTS_CHARTS_COLOR } from "../../data/communityInsightsData.ts";
import { RevCard, RevEmptyState, RevTooltip } from "@revmatics/catalyst";
import { fullFormatNumbers } from "../../../../../../../../helpers/functions/formatNumber.ts";
import { getFitName } from "../../../../../../../../helpers/functions/getFitName.ts";

interface DonutChartData {
  id: string;
  label: string;
  value: number;
  color: string;
}

export const DonutChart = () => {
  const [emptyState, setEmptyState] = useState(false);
  const communityAggregate = useAppSelector(
    (state) => state.discoverCommunities.communityAggregate,
  );

  const result: DonutChartData[] = useMemo(() => {
    if (communityAggregate && communityAggregate.donut_chart) {
      setEmptyState(false);
      return Object.keys(communityAggregate.donut_chart)
        .filter((elem) => elem !== "NULL" && elem !== "total")
        .map((key, index) => {
          const typedKey = key as keyof typeof communityAggregate.donut_chart;
          return {
            id: key.toLowerCase(),
            label: key.toLowerCase(),
            value: communityAggregate.donut_chart[typedKey],
            color: COMMUNITY_INSIGHTS_CHARTS_COLOR[index].color,
          };
        });
    } else {
      setEmptyState(true);
      return [];
    }
  }, [communityAggregate]);

  return (
    <RevCard>
      <div>
        <h6>Fit</h6>
      </div>
      <div className="h-[222px] lg:h-[364px] relative">
        {emptyState && (
          <RevEmptyState
            text="If you’re seeing this message, we don’t have enough data to complete these views."
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        )}
        {!emptyState && communityAggregate?.donut_chart.total && (
          <div className="flex flex-col gap-2 items-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <p className="typography-body-sm text-monochrome-500">Total size</p>
            <h4 data-testid="total-size">
              {fullFormatNumbers({
                value: communityAggregate?.donut_chart.total,
                shortenValue: 1e3,
              })}
            </h4>
          </div>
        )}
        <ResponsivePie
          data={result}
          innerRadius={0.6}
          padAngle={0}
          borderWidth={2}
          borderColor="#fff"
          margin={{ top: 30, right: 7, bottom: 30, left: 7 }}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          activeOuterRadiusOffset={7}
          colors={{
            datum: "data.color",
          }}
          tooltip={({ datum }) => {
            return (
              <RevTooltip
                position="bottom"
                className="min-w-[150px] max-w-[220px] z-[9999]"
                wrapperClassName="p-0 overflow-hidden bg-white shadow-elevation-2"
              >
                <div className="bg-white px-3 py-2 flex flex-col gap-1">
                  <div className="grid grid-cols-[8px_1fr] gap-3 items-center">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        COMMUNITY_INSIGHTS_CHARTS_COLOR[datum.arc.index]?.bg ||
                        "bg-monochrome-300"
                      }`}
                    />

                    <div className="flex justify-between items-center gap-4">
                      <span className="typography-body-xs text-monochrome-600 capitalize">
                        {getFitName(datum.label.toString())}
                      </span>
                      <span className="typography-body-bold-xs text-monochrome-900">
                        {fullFormatNumbers({ value: datum.value })}
                      </span>
                    </div>
                  </div>
                </div>
              </RevTooltip>
            );
          }}
        />
      </div>
      <div className="flex flex-wrap gap-4 items-center">
        {result.map((item, index) => {
          return (
            <div
              className="flex items-center gap-2"
              key={`rev-legend-charts-donut-discover-${index}`}
            >
              <div
                className={`w-3 h-3 rounded-xs ${COMMUNITY_INSIGHTS_CHARTS_COLOR[index].bg}`}
              />
              <p className="typography-body-xs text-monochrome-900 capitalize">
                {getFitName(item.id.toLowerCase())}
              </p>
            </div>
          );
        })}
      </div>
    </RevCard>
  );
};
